import { Storage } from "aws-amplify";

export interface ExistingPhoto {
  relPath: string;
  name: string;
  section: string;
  url: string;
}

function sectionRank(section: string): number {
  const s = (section || "").toLowerCase();
  return s === "cover" ? 0 : s === "packshot" ? 1 : 2;
}
function photoNumber(fileName: string): number {
  const base = fileName.replace(/\.[^.]+$/, "");
  const parts = base.split("_");
  const maybe = parts[parts.length - 2] ?? "";
  return /^\d+$/.test(maybe) ? Number(maybe) : 0;
}
function comparePhotos(a: { section: string; name: string }, b: { section: string; name: string }): number {
  const bySection = sectionRank(a.section) - sectionRank(b.section);
  if (bySection !== 0) return bySection;
  const byNumber = photoNumber(a.name) - photoNumber(b.name);
  if (byNumber !== 0) return byNumber;
  return a.name.localeCompare(b.name);
}

/** Lists images already in the system for a product, with signed preview URLs. */
export async function listExistingPhotos(barcode: string, matCode: string): Promise<ExistingPhoto[]> {
  const prefix = `files/thumbnail/${barcode}/${matCode}/`;
  const res: any = await Storage.list(prefix, { level: "public", pageSize: 1000 });
  const objects = (res.results ?? []).filter(
    (o: any) => o.key && o.size > 0 && /\.(jpe?g|png)$/i.test(o.key)
  );
  const photos = await Promise.all(
    objects.map(async (o: any): Promise<ExistingPhoto> => {
      const relPath = o.key.replace(/^files\/thumbnail\//, "");
      const parts = relPath.split("/");
      const url = await Storage.get(o.key, { level: "public" });
      return { relPath, name: parts[parts.length - 1], section: parts[2] ?? "", url: url as string };
    })
  );
  return photos.sort((a, b) =>
    comparePhotos({ section: a.section, name: a.name }, { section: b.section, name: b.name })
  );
}

/** Derived keys (original/<rel>) whose relPath already exists in the system. */
export function findConflicts(derivedKeys: string[], existingRelPaths: string[]): Set<string> {
  const existing = new Set(existingRelPaths);
  return new Set(derivedKeys.filter((k) => existing.has(k.replace(/^original\//, ""))));
}
